// AI consent layer.
//
// Apple guideline 5.1.1(i) / 5.1.2(i) requires that, before an app sends
// personal data to a third-party AI service, it must (in-app, not just in
// the privacy policy):
//   1. Disclose what data will be sent
//   2. Identify who the data is sent to
//   3. Obtain the user's permission
//
// This module owns the persisted consent flag and a tiny pub/sub used by
// the global consent modal mounted in App.jsx. The lib functions that talk
// to Anthropic / ElevenLabs / OpenAI Whisper (callLLM, generateBrief,
// fetchAudio, transcribeAudio) all gate themselves on `requestAIConsent()`
// before issuing the network call, so a missed UI gate cannot silently
// leak data.

const STORAGE_KEY = 'proxera.ai-consent.v1';
const CURRENT_VERSION = 1;

// ── Persisted state ────────────────────────────────────────────────
// We cache the parsed consent value in a module-level variable. This
// matters for `useSyncExternalStore` callers (the Privacy toggle): they
// require getSnapshot to return a stable reference between renders when
// nothing has changed, otherwise React loops or errors. Re-parsing
// localStorage on every call would return a new object each time and
// crash the page.

let cachedConsent = readFromStorage();
let cacheReady = true;

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.granted !== true) return null;
    if (parsed.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getConsent() {
  if (!cacheReady) {
    cachedConsent = readFromStorage();
    cacheReady = true;
  }
  return cachedConsent;
}

export function hasConsent() {
  return !!getConsent();
}

export function grantConsent() {
  const value = {
    granted: true,
    grantedAt: new Date().toISOString(),
    version: CURRENT_VERSION,
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch {}
  cachedConsent = value;
  cacheReady = true;
  emitState();
  return value;
}

export function revokeConsent() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  cachedConsent = null;
  cacheReady = true;
  emitState();
}

// Cross-tab / cross-context sync. If localStorage is mutated outside this
// module (another tab on web, or the OS clearing storage), invalidate the
// cache so the next read picks up the fresh value.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      cacheReady = false;
      emitState();
    }
  });
}

// ── Subscribers ────────────────────────────────────────────────────
// Components that show consent state (Profile toggle) subscribe here so
// they re-render when consent is granted or revoked elsewhere.

const stateListeners = new Set();
export function subscribeConsent(fn) {
  stateListeners.add(fn);
  return () => stateListeners.delete(fn);
}
function emitState() {
  for (const fn of stateListeners) {
    try { fn(); } catch {}
  }
}

// ── Modal request queue ────────────────────────────────────────────
// `requestAIConsent()` is the single entry point any feature uses before
// touching a third-party AI service. If consent is already on file it
// resolves true immediately. Otherwise it asks the global modal to open
// and waits for the user's answer. Concurrent callers all resolve with
// the same answer.

const modalListeners = new Set();
let modalOpen = false;
let pendingResolvers = [];

export function subscribeModal(fn) {
  modalListeners.add(fn);
  return () => modalListeners.delete(fn);
}
function emitModal() {
  for (const fn of modalListeners) {
    try { fn(); } catch {}
  }
}

export function isModalOpen() {
  return modalOpen;
}

export function requestAIConsent() {
  if (hasConsent()) return Promise.resolve(true);
  return new Promise((resolve) => {
    pendingResolvers.push(resolve);
    if (!modalOpen) {
      modalOpen = true;
      emitModal();
    }
  });
}

// Resolve every pending request with the user's choice. Called by the
// modal's button handlers.
export function resolveAIConsent(granted) {
  if (granted) grantConsent();
  modalOpen = false;
  emitModal();
  const queue = pendingResolvers;
  pendingResolvers = [];
  for (const r of queue) {
    try { r(granted); } catch {}
  }
}

// Used when a feature starts a request, the user backgrounds the app
// before answering, and we want to discard the queue cleanly. Currently
// unused; exported for completeness.
export function cancelPendingAIConsent() {
  resolveAIConsent(false);
}

// Custom error class so the catch sites in the scenes can distinguish a
// declined-consent abort from a real backend failure and avoid showing
// the user a scary error toast.
export class AIConsentDeclinedError extends Error {
  constructor(message = 'AI consent declined.') {
    super(message);
    this.name = 'AIConsentDeclinedError';
  }
}

// Helper used by lib functions: ask for consent, throw if declined.
export async function ensureAIConsent() {
  const granted = await requestAIConsent();
  if (!granted) throw new AIConsentDeclinedError();
  return true;
}
